import { Component, OnInit, inject, ChangeDetectorRef, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Auth } from '@angular/fire/auth';
import { EmailFormComponent } from '../../email-form/email-form.component';
import { PdfGeneratorService } from '../../services/pdf-generator.service';
import { InvoiceService } from '../../services/invoice.service';
import { AccessService } from '../../services/access.service';
import { SalesInvoiceService } from '../../services/sales-invoice.service';
import { SalesInvoiceTemplateComponent } from './sales-invoice-template.component';
import { firstValueFrom } from 'rxjs';

interface SalesInvoiceItem {
  item: string;
  quantity: number;
  unitPrice: number;
  amount: number;
}

@Component({
  selector: 'app-sales-invoice',
  standalone: true,
  imports: [CommonModule, FormsModule, EmailFormComponent, SalesInvoiceTemplateComponent],
  templateUrl: './sales-invoice.component.html',
  styleUrl: './sales-invoice.component.css'
})
export class SalesInvoiceComponent implements OnInit {
  auth = inject(Auth);
  invoiceService = inject(InvoiceService);
  pdfService = inject(PdfGeneratorService);
  accessService = inject(AccessService);
  salesInvoiceService = inject(SalesInvoiceService);
  cd = inject(ChangeDetectorRef);

  activeTab: 'invoices' | 'items' = 'invoices';

  gridData: any[] = [];
  filteredGridData: any[] = [];
  companies: any[] = [];
  feeCollectionCompanies: any[] = [];
  isLoadingGrid = false;

  // Filters
  filterCompanyId: string = '';
  expandedRowId: string | null = null;

  // Sorting
  sortColumn: string = '';
  sortDirection: 'asc' | 'desc' = 'asc';

  // Modals
  isManualModalOpen = false;
  isInvoiceModalOpen = false;
  isEmailModalOpen = false;
  isSavingPDFs = false;

  // Form State
  templateItems: any[] = [];
  isItemModalOpen = false;
  isItemEditMode = false;
  editingTemplateItemId: string | null = null;
  templateItemData = { name: '', defaultUnitPrice: 0 };
  isLoadingItems = false;
  isSavingItem = false;

  isSenderModalOpen = false;
  senderSearchQuery = '';
  availableSenderIds: string[] = [];
  tempSelectedSenderIds: Set<string> = new Set();

  manualInvoiceData: {
    companyId: string;
    senderId: string;
    invoiceDate: string;
    items: SalesInvoiceItem[];
    totalDue: number;
    amountPaid: number;
  } = { companyId: '', senderId: '', invoiceDate: '', items: [], totalDue: 0, amountPaid: 0 };

  isManualEditMode = false;
  editingInvoiceId: string | null = null;

  get selectedCompanyFedexNumber(): string | null {
    if (!this.manualInvoiceData?.companyId) return null;
    const company = this.companies.find(c => c.id === this.manualInvoiceData.companyId);
    return company?.fedexNumber || null;
  }

  getSenderCompanyName(): string {
    if (!this.manualInvoiceData.senderId) return '';
    const company = this.companies.find(c => c.id === this.manualInvoiceData.senderId);
    return company ? company.company : '';
  }

  get availableSenders(): any[] {
    return this.companies.filter(c => this.availableSenderIds.includes(c.id));
  }

  get filteredSenderCompanies(): any[] {
    if (!this.senderSearchQuery) return this.companies;
    const query = this.senderSearchQuery.toLowerCase();
    return this.companies.filter(c => (c.company || '').toLowerCase().includes(query));
  }

  // View state
  selectedInvoice: any = null;

  // Email State
  emailData: any = { to: '', subject: '', body: '', attachments: [] };
  currentEmailRow: any = null;

  ngOnInit() {
    this.loadInitialData();
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscKey(event: Event) {
    if (this.isManualModalOpen) this.closeManualModal();
    if (this.isInvoiceModalOpen) this.closeInvoiceModal();
    if (this.isItemModalOpen) this.closeItemModal();
    if (this.isEmailModalOpen) this.closeEmailModal(false);
  }

  async loadInitialData() {
    this.isLoadingGrid = true;
    try {
      this.companies = await firstValueFrom(this.invoiceService.getCompanies());
      this.companies.sort((a, b) => (a.company || '').localeCompare(b.company || ''));
      // Note: Adjust this filter depending on how your DB identifies "Fee Collection" companies
      this.feeCollectionCompanies = this.companies.filter(c => c.companyType === 'Fee Collection' || c.type === 'Fee Collection' || c.isFeeCollection);
      await this.loadTemplateItems();
      await this.loadInvoices();
    } catch (e) {
      console.error('Error loading data', e);
    }
  }

  async loadTemplateItems() {
    this.isLoadingItems = true;
    try {
      this.templateItems = await this.salesInvoiceService.getTemplateItems();
      this.templateItems.sort((a: any, b: any) => a.name.localeCompare(b.name));
    } catch(e) {
      console.error('Error loading items', e);
    } finally {
      this.isLoadingItems = false;
      this.cd.detectChanges();
    }
  }

  async loadInvoices() {
    this.isLoadingGrid = true;
    try {
      this.gridData = await this.salesInvoiceService.getSalesInvoices();
      this.gridData.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime() ? -1 : 1);
      this.applyFilters();
    } catch (e) {
      console.error('Error loading sales invoices', e);
    } finally {
      this.isLoadingGrid = false;
      this.cd.detectChanges();
    }
  }

  applyFilters() {
    this.filteredGridData = this.gridData.filter(row => {
      if (this.filterCompanyId && row.companyId !== this.filterCompanyId) return false;
      return true;
    });
    this.executeSort();
  }

  sortBy(column: string) {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.executeSort();
  }

  executeSort() {
    if (!this.sortColumn) return;
    const column = this.sortColumn;
    const direction = this.sortDirection === 'asc' ? 1 : -1;

    this.filteredGridData.sort((a, b) => {
      let valA = column === 'company' ? (a.company?.company || '') : a[column];
      let valB = column === 'company' ? (b.company?.company || '') : b[column];

      if (typeof valA === 'string') valA = valA.toLowerCase();
      if (typeof valB === 'string') valB = valB.toLowerCase();

      if (valA == null) valA = '';
      if (valB == null) valB = '';

      if (valA < valB) return -1 * direction;
      if (valA > valB) return 1 * direction;
      return 0;
    });
  }

  switchTab(tab: 'invoices' | 'items') {
    this.activeTab = tab;
  }

  openAddInvoiceModal() {
    this.isManualEditMode = false;
    this.editingInvoiceId = null;
    this.manualInvoiceData = {
      companyId: '',
      senderId: '',
      invoiceDate: new Date().toISOString().substring(0, 10),
      items: [],
      totalDue: 0,
      amountPaid: 0
    };
    this.isManualModalOpen = true;
  }

  openEditInvoiceModal(row: any) {
    this.isManualEditMode = true;
    this.editingInvoiceId = row.id;
    this.manualInvoiceData = {
      companyId: row.companyId,
      senderId: row.senderId || '',
      invoiceDate: row.invoiceDate,
      items: JSON.parse(JSON.stringify(row.items || [])),
      totalDue: row.totalDue || 0,
      amountPaid: row.amountPaid || 0
    };
    if (this.manualInvoiceData.senderId && !this.availableSenderIds.includes(this.manualInvoiceData.senderId)) {
      this.availableSenderIds.push(this.manualInvoiceData.senderId);
    }
    this.isManualModalOpen = true;
  }

  closeManualModal() {
    this.isManualModalOpen = false;
  }

  openSenderSelectionModal() {
    this.senderSearchQuery = '';
    this.tempSelectedSenderIds = new Set(this.availableSenderIds);
    this.isSenderModalOpen = true;
  }

  closeSenderSelectionModal() {
    this.isSenderModalOpen = false;
  }

  toggleSenderSelection(companyId: string) {
    if (this.tempSelectedSenderIds.has(companyId)) {
      this.tempSelectedSenderIds.delete(companyId);
    } else {
      this.tempSelectedSenderIds.add(companyId);
    }
  }

  confirmSenderSelection() {
    this.availableSenderIds = Array.from(this.tempSelectedSenderIds);
    // If the currently selected sender is no longer in the list, clear it
    if (this.manualInvoiceData.senderId && !this.availableSenderIds.includes(this.manualInvoiceData.senderId)) {
      this.manualInvoiceData.senderId = '';
    }
    this.closeSenderSelectionModal();
  }

  addInvoiceItem() {
    this.manualInvoiceData.items.push({ item: '', quantity: 1, unitPrice: 0, amount: 0 });
    this.calculateTotal();
  }

  removeInvoiceItem(index: number) {
    this.manualInvoiceData.items.splice(index, 1);
    this.calculateTotal();
  }

  onItemSelectionChange(item: SalesInvoiceItem) {
    const template = this.templateItems.find(t => t.name === item.item);
    if (template) {
      item.unitPrice = template.defaultUnitPrice || 0;
      this.calculateItemAmount(item);
    }
  }

  calculateItemAmount(item: SalesInvoiceItem) {
    item.amount = (item.quantity || 0) * (item.unitPrice || 0);
    this.calculateTotal();
  }

  calculateTotal() {
    this.manualInvoiceData.totalDue = this.manualInvoiceData.items.reduce((sum, item) => sum + item.amount, 0);
  }

  async saveManualInvoice() {
    this.isLoadingGrid = true;
    try {
      const company = this.companies.find(c => c.id === this.manualInvoiceData.companyId);
      const sender = this.feeCollectionCompanies.find(c => c.id === this.manualInvoiceData.senderId) || this.companies.find(c => c.id === this.manualInvoiceData.senderId);

      let totalDue = this.manualInvoiceData.totalDue;
      let amountPaid = this.manualInvoiceData.amountPaid || 0;
      let remainingBalance = totalDue - amountPaid;
      let status = remainingBalance <= 0.01 ? 'Paid' : (amountPaid > 0.01 ? 'Partial' : 'Not Paid');

      const payload: any = {
        companyId: company.id,
        company: company,
        senderId: this.manualInvoiceData.senderId,
        sender: sender || null,
        invoiceDate: this.manualInvoiceData.invoiceDate,
        items: this.manualInvoiceData.items,
        totalDue,
        remainingBalance,
        amountPaid,
        status,
        updatedAt: new Date().toISOString()
      };

      if (!this.isManualEditMode) {
        payload.createdAt = new Date().toISOString();
        payload.emailStatus = 'Not Sent';
        const dateStr = payload.invoiceDate.replace(/-/g, '');
        const compSuffix = company.id.substring(0, 5).toUpperCase();
        payload.invoiceNumber = `SI-${dateStr}-${compSuffix}-${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`;
      }

      await this.salesInvoiceService.saveSalesInvoice(payload, this.isManualEditMode && this.editingInvoiceId ? this.editingInvoiceId : null);
      this.closeManualModal();
      await this.loadInvoices();
    } catch (e) {
      console.error("Error saving invoice", e);
      alert('Failed to save sales invoice.');
    } finally {
      this.isLoadingGrid = false;
    }
  }

  async deleteInvoice(row: any) {
    if (!confirm(`Are you sure you want to delete invoice ${row.invoiceNumber}?`)) return;
    try {
      await this.salesInvoiceService.deleteSalesInvoice(row.id);
      this.gridData = this.gridData.filter(r => r.id !== row.id);
      this.applyFilters();
    } catch (e) {
      console.error(e);
      alert('Failed to delete invoice.');
    }
  }

  viewInvoice(row: any) {
    const latestCompany = this.companies.find(c => c.id === row.companyId) || row.company;
    this.selectedInvoice = { ...row, company: latestCompany };
    this.isInvoiceModalOpen = true;
  }

  closeInvoiceModal() {
    this.isInvoiceModalOpen = false;
    this.selectedInvoice = null;
  }

  async printInvoice(invoice: any) {
    await this.handlePdfAction(invoice, 'open');
  }

  private async handlePdfAction(invoice: any, action: 'save' | 'open' | 'blob'): Promise<Blob | void> {
    const latestCompany = this.companies.find(c => c.id === invoice.companyId) || invoice.company;
    const updatedInvoice = { ...invoice, company: latestCompany };

    let pdfWindow: Window | null = null;
    if (action === 'open') {
      pdfWindow = window.open('', '_blank');
      if (pdfWindow) {
        pdfWindow.document.write('<html><body><div style="display:flex;justify-content:center;align-items:center;height:100vh;font-family:sans-serif;"><div>Generating PDF... Please wait.</div></div></body></html>');
      }
    }

    this.selectedInvoice = updatedInvoice;
    this.cd.detectChanges();
    await new Promise(resolve => setTimeout(resolve, 500)); // wait for DOM to update

    const content = document.getElementById('pdf-content');
    if (content) {
      const filename = `Sales_Invoice_${updatedInvoice.invoiceNumber}.pdf`;
      try {
        if (action === 'save') {
          await this.pdfService.savePdf(content, filename, 'portrait');
        } else if (action === 'open') {
          await this.pdfService.openPdf(content, filename, 'portrait', pdfWindow || undefined);
        } else if (action === 'blob') {
          return await this.pdfService.generateBlob(content, filename);
        }
      } catch (error) {
        if (pdfWindow) pdfWindow.close();
        throw error;
      }
    } else {
      if (pdfWindow) pdfWindow.close();
    }
  }

  async emailSingleInvoice(row: any) {
    const latestCompany = this.companies.find(c => c.id === row.companyId) || row.company;
    const updatedRow = { ...row, company: latestCompany };
    this.currentEmailRow = updatedRow;
    this.isSavingPDFs = true;
    try {
      const pdfBlob = await this.handlePdfAction(updatedRow, 'blob') as Blob;
      const user = this.auth.currentUser;
      
      const { downloadURL, storagePath } = await this.salesInvoiceService.uploadTempPdf(user?.uid || 'temp', updatedRow.invoiceNumber, pdfBlob);

      let senderName = 'LFC Franchise Inc.';
      if (updatedRow.sender?.company) {
        senderName = updatedRow.sender.company;
      } else {
        const brand = (updatedRow.company?.brand || '').trim();
        senderName = brand === 'K-Photo Lavie' ? 'KCE Group' : 'LFC Franchise Inc.';
      }

      let body = `Dear ${updatedRow.company.pocName || 'Valued Customer'},\n\nPlease find attached your sales invoice (${updatedRow.invoiceNumber}) for the amount of $${updatedRow.totalDue}.\n\nThank you for your business!\n\nBest regards,\n${senderName}`;

      if (user) {
        try {
          const template = await this.accessService.getEmailTemplate(user.uid, 'sales_invoice');
          if (template) body = template;
        } catch (e) {
          console.error('Error loading email template', e);
        }
      }

      this.emailData = {
        to: updatedRow.company.email || '',
        subject: `Sales Invoice ${updatedRow.invoiceNumber} - ${updatedRow.company.company}`,
        body: body,
        attachments: [{
          name: `Invoice_${updatedRow.invoiceNumber}.pdf`,
          url: downloadURL,
          storagePath: storagePath
        }]
      };
      this.isEmailModalOpen = true;
    } catch (error) {
      console.error('Error generating email attachment:', error);
      alert('Failed to generate invoice attachment.');
    } finally {
      this.isSavingPDFs = false;
    }
  }

  async onEmailSent(event?: any) {
    if (this.currentEmailRow) {
      const row = this.currentEmailRow;
      try {
        await this.salesInvoiceService.updateInvoiceEmailStatus(row.id, this.emailData.to || '');
        row.emailStatus = 'Sent';
        row.emailSentAt = new Date().toISOString();
        row.emailRecipients = this.emailData.to;
        this.cd.detectChanges();
      } catch(e) {
        console.error("Failed to update email status", e);
      }
    }
    this.closeEmailModal(false);
  }

  async closeEmailModal(shouldDelete: boolean = false) {
    if (shouldDelete && this.emailData.attachments && this.emailData.attachments.length > 0) {
      for (const attachment of this.emailData.attachments) {
        if (attachment.storagePath) {
          try {
            await this.salesInvoiceService.deleteTempPdf(attachment.storagePath);
          } catch (e) {}
        }
      }
    }
    this.isEmailModalOpen = false;
    this.emailData = { to: '', subject: '', body: '', attachments: [] };
    this.currentEmailRow = null;
  }

  async updateAmountPaid(row: any) {
    if (row.amountPaid < 0) row.amountPaid = 0;
    if (row.amountPaid > row.totalDue) row.amountPaid = row.totalDue;
    
    row.amountPaid = Math.round(Number(row.amountPaid) * 100) / 100;
    row.remainingBalance = Math.round((row.totalDue - row.amountPaid) * 100) / 100;
    row.status = row.remainingBalance <= 0.01 ? 'Paid' : (row.amountPaid > 0.01 ? 'Partial' : 'Not Paid');
    
    this.saveStatusUpdate(row);
  }

  async updateInvoiceStatus(row: any, newStatus: string) {
    row.status = newStatus;
    if (newStatus === 'Paid') {
      row.amountPaid = row.totalDue;
      row.remainingBalance = 0;
    } else if (newStatus === 'Not Paid') {
      row.amountPaid = 0;
      row.remainingBalance = row.totalDue;
    }
    this.saveStatusUpdate(row);
  }

  private async saveStatusUpdate(row: any) {
    try {
      await this.salesInvoiceService.updateInvoiceStatus(row.id, {
        amountPaid: row.amountPaid,
        remainingBalance: row.remainingBalance,
        status: row.status
      });
      this.cd.detectChanges();
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status.');
    }
  }

  // --- Invoice Items Tab Actions ---

  openAddItemModal() {
    this.isItemEditMode = false;
    this.editingTemplateItemId = null;
    this.templateItemData = { name: '', defaultUnitPrice: 0 };
    this.isItemModalOpen = true;
  }

  openEditItemModal(item: any) {
    this.isItemEditMode = true;
    this.editingTemplateItemId = item.id;
    this.templateItemData = { name: item.name, defaultUnitPrice: item.defaultUnitPrice || 0 };
    this.isItemModalOpen = true;
  }

  closeItemModal() {
    this.isItemModalOpen = false;
  }

  async saveTemplateItem() {
    this.isSavingItem = true;
    try {
      if (this.isItemEditMode && this.editingTemplateItemId) {
        await this.salesInvoiceService.updateTemplateItem(this.editingTemplateItemId, this.templateItemData);
      } else {
        await this.salesInvoiceService.addTemplateItem(this.templateItemData);
      }
      this.closeItemModal();
      await this.loadTemplateItems();
    } catch(e) {
      console.error(e);
      alert('Failed to save item.');
    } finally {
      this.isSavingItem = false;
    }
  }

  async deleteTemplateItem(item: any) {
    if(!confirm(`Are you sure you want to delete item ${item.name}?`)) return;
    try {
      await this.salesInvoiceService.deleteTemplateItem(item.id);
      await this.loadTemplateItems();
    } catch(e) {
      console.error(e);
      alert('Failed to delete item.');
    }
  }
}
